import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useMsal } from '@azure/msal-react';
import type { AccountInfo } from '@azure/msal-browser';
import { AUTH_BYPASS, MAJANS_TENANT_ID } from '../config/auth';

/**
 * Who am I signed in as, and how do I get out.
 *
 * The app had NO sign-out at all. Two separate error paths in config/auth.ts end with
 * "sign out and sign back in" — the documented recovery for a stuck token renewal and
 * the ONLY recovery for being signed in on a non-Majans account — and neither was
 * possible from the UI. Once you held a broken token you never saw AuthGate again,
 * because it only renders when MSAL reports you unauthenticated. A stale token is not
 * unauthenticated, so the advice was unreachable by construction.
 *
 * `Sign out` clears MSAL's cache and returns to the sign-in screen. `Reset session`
 * exists separately because they fix different things: sign-out is the account-level
 * fix, while a wedged silent-renewal is local cache state — including the
 * once-a-minute redirect guard in sessionStorage, which can otherwise suppress the very
 * redirect that would have recovered the session.
 */
export default function AccountMenu(): ReactElement | null {
  const { instance, accounts } = useMsal();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      // `contains` wants a Node; `EventTarget` is the wider DOM type. Asserted rather
      // than `instanceof`-narrowed so the `ref.current &&` guard stays the only guard —
      // adding a second one would change what happens for a non-Node target.
      if (ref.current && !ref.current.contains(e.target as Node | null)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  if (AUTH_BYPASS) return null;

  const active: AccountInfo | null = instance.getActiveAccount() ?? accounts[0] ?? null;
  const username = active?.username ?? 'Not signed in';
  const initial = (username[0] ?? '?').toUpperCase();
  // Surfaced because it is the difference between "the app is broken" and "you are on
  // the wrong account" — a non-Majans account can never be issued a token for this API.
  const wrongTenant = Boolean(active && active.tenantId !== MAJANS_TENANT_ID);

  const signOut = async () => {
    try {
      sessionStorage.removeItem('purchasing:tokenRedirectAt');
    } catch {
      /* sessionStorage can be unavailable in hardened browser modes */
    }
    await instance.logoutRedirect({ account: active ?? undefined });
  };

  const resetSession = () => {
    // Clear only this app's local auth state, then reload. Enough to unwedge a stale
    // token or a tripped redirect guard without making the user re-enter credentials.
    try {
      sessionStorage.removeItem('purchasing:tokenRedirectAt');
      Object.keys(localStorage)
        .filter((k) => k.startsWith('msal.') || k.includes('login.microsoftonline.com'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore — the reload below still gives MSAL a clean attempt */
    }
    window.location.reload();
  };

  return (
    <div className="account-menu" ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={username}
      >
        <span aria-hidden="true">{wrongTenant ? '⚠' : initial}</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            minWidth: 260,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-soft)',
            borderRadius: 8,
            padding: 10,
            zIndex: 50,
            boxShadow: '0 6px 20px rgba(0,0,0,.18)',
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.8, wordBreak: 'break-all', marginBottom: 8 }}>
            {username}
          </div>
          {wrongTenant && (
            <div style={{ fontSize: 11.5, marginBottom: 8, color: 'var(--danger, #c0392b)' }}>
              This is not a Majans account, so no token for this app can be issued. Sign out
              and sign back in with your @majans.com account.
            </div>
          )}
          <button className="btn" style={{ width: '100%', marginBottom: 6 }} onClick={resetSession}>
            Reset session
          </button>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={signOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
